import './CollaboratorDetail.scss'

import {
	Contentside,
	ContentsideContent,
	ContentsideSidebar
} from '../layout/Contentside'
import {EntryData, ImageType, WysiwygType} from '../../types'
import {NewsBlock, NewsBlockType} from '../blocks/NewsBlock'

import {Breadcrumbs} from '../layout/Breadcrumbs'
import {Hero} from '../layout/Hero'
import {Layout} from '../layout/Layout'
import {Link} from '../../util/link'
import {ListItemBlock} from '../blocks/ListBlock'
import React from 'react'
import {SearchResultImage} from '../layout/search/SearchResult'
import {SidebarBlocks} from '../sidebar/SidebarBlocks'
import {Theme} from '../Theme'
import placeholder from 'assets/images/placeholder.png'
import {style} from '../../util/style'
import {useTranslation} from '../../util/i18n'
import {wrap} from '@codeurs/front'

const styles = style('collaboratordetail', ['news', 'item'])

export type CollaboratorDetailType = {
	image: ImageType
	text: WysiwygType
	email: string
	info?: WysiwygType
	news?: Array<NewsBlockType>
}

type CollaboratorNewsItemType = {
	url: string
	title: string
	hero_image: ImageType
	hero_text: WysiwygType
	hero_title: string
	hero_subtitle: string
}

export const CollaboratorDetail: React.FC<CollaboratorDetailType> = wrap(
	Layout
)(({...props}) => {
	const {
		name,
		image,
		text,
		email,
		info,
		hero,
		parents,
		sidebarblocks,
		news
	} = props

	const emailCta = {
		type: 'external' as const,
		url: email ? `mailto:${email}` : null,
		title: email,
		target: '_self' as const
	}

	return (
		<div className={styles()}>
			<Hero {...hero} title={(hero && hero.name) || name} />
			<Breadcrumbs parents={parents} title={name} />
			<Contentside>
				<ContentsideContent>
					<ListItemBlock
						url=""
						title={name}
						preview_image={image || placeholder}
						preview_text={text}
						cta={emailCta}
					/>
					<div className={styles('info')}>
						<Theme.Wysiwyg>{info}</Theme.Wysiwyg>
					</div>
					<CollaboratorDetailNews news={news} />
				</ContentsideContent>
				<ContentsideSidebar>
					<SidebarBlocks blocks={sidebarblocks} />
				</ContentsideSidebar>
			</Contentside>
		</div>
	)
})

const CollaboratorDetailNews: React.FC<{
	news?: Array<CollaboratorNewsItemType>
}> = ({news}) => {
	if (!news || news.length === 0) return null

	return (
		<div className={styles.news()}>
			{news.map((item: CollaboratorNewsItemType, i) => (
				<CollaboratorDetailNewsItem key={i} {...item} />
			))}
		</div>
	)
}

const CollaboratorDetailNewsItem: React.FC<CollaboratorNewsItemType> = ({
	url,
	title: pageTitle,
	hero_image,
	hero_title,
	hero_subtitle,
	hero_text
}) => {
	const t = useTranslation('news')
	const title = hero_title || pageTitle
	const subtitle = hero_subtitle
	const text = hero_text
	const image = hero_image

	return (
		<div className={styles.item()}>
			<div className={styles.item('row')}>
				<div className={styles.item('row-image')}>
					<Link to={url}>
						<SearchResultImage image={image} />
					</Link>
				</div>
				{title && (
					<Theme.H2 as="h3" className={styles.item('row-title')} mod="green">
						<Link to={url}>{title}</Link>
					</Theme.H2>
				)}
			</div>
			<div className={styles.item('content')}>
				{subtitle && (
					<Theme.H5 as="h4" className={styles.item('content-custom_subtitle')}>
						{subtitle}
					</Theme.H5>
				)}
				{text && (
					<div className={styles.item('content-text')}>
						<Theme.Wysiwyg>{text}</Theme.Wysiwyg>
					</div>
				)}
			</div>
			{url && (
				<div className={styles.item('link')}>
					<Theme.Link to={url} iconafter="chevronRight">
						{t.readmore}
					</Theme.Link>
				</div>
			)}
			{url && (
				<div className={styles.item('url')}>
					<Link to={url} className={styles.item('url-link')}>
						{location.protocol + url}
					</Link>
				</div>
			)}
		</div>
	)
}
